import { ExternalProduct, InternalProduct, Product } from '../models/product/types';
import {
    dedupeExternalCatalogProducts,
    getExternalInitialMfFields,
} from './tcExternalProducts';
import { normalizeProductMasterFormat } from '../pages/projectInfo/tabs/ReportingTab/tabs/LeedMEP/leedMepTopicRules';

const DEFAULT_MF_DIVISION = { label: 'General Requirements', number: '01 00 00' };
const DEFAULT_MF_SECTION = { label: 'General', number: '01 00 00' };

/**
 * Merges internal project products with Transparency Catalog (TC) external rows.
 *
 * Bug context (892): After add/copy, the products list refetched internal IDs but TC lookup
 * could miss new or manual (OutsideTC) rows, so merged list appeared empty until cache clear.
 * Fallback external shape keeps those products visible without a hard refresh.
 */
const createFallbackExternalProduct = (internal: InternalProduct): ExternalProduct => {
    const isManual = internal.storage === 'OutsideTC';

    return {
        id: internal.productId,
        type: isManual ? 'userProducts' : 'product',
        status: 'live',
        productName: internal.name || `Product ${internal.productId}`,
        company: '',
        companyID: 0,
        transparencyCatalogUrl: internal.transparencyCatalogUrl || '',
        mfDivision: DEFAULT_MF_DIVISION,
        mfSection: DEFAULT_MF_SECTION,
        mfSectionLevel1: DEFAULT_MF_SECTION,
        lowestLevelNumber: DEFAULT_MF_SECTION.number,
        lowestLevelLabel: DEFAULT_MF_SECTION.label,
        level: null,
        hasEpd: false,
        hasHpd: false,
        hasDeclare: false,
        hasC2c: false,
        hasC2cMHC: false,
        hasLpc: false,
        epdID: '',
        epdUrl: null,
        hpdUrl: null,
        declareUrl: null,
        lpcResults: null,
        c2cCertified: '',
        c2cMHC: '',
        c2cMHCExpiresDate: '',
        c2cMHCResults: '',
        c2cResults: '',
        declare: '',
        declareExpiresDate: '',
        declareResults: '',
        ec3EmbodiedCarbon: '',
        ec3Url: '',
        epdType: '',
        epdProgram: '',
        epdExpiresDate: '',
        optimizedEpdUrl: '',
        hpd: '',
        lpc: '',
        hpdExpiresDate: '',
        hpdScopeResults: '',
        hpdScopeResultsCorrected: '',
        listingHTML: '',
        scopeRegion: '',
        description: internal.description,
        mfrInv: '',
        mfrInvResults: '',
        disclosures: [],
        hasCleanAirSilver: false,
        cleanAirSilverExpires: '',
        cleanAirSilverUrl: '',
        hasCleanAirGold: false,
        cleanAirGoldExpires: '',
        cleanAirGoldUrl: '',
        hasClearchem: false,
        clearchemExpires: '',
        clearchemUrl: '',
        hasFloorscore: false,
        floorscoreExpires: '',
        floorscoreUrl: '',
        hasGreenCertified: false,
        greenCertifiedExpires: '',
        greenCertifiedUrl: '',
        hasGreenguard: false,
        greenguardExpires: '',
        greenguardUrl: '',
        hasGreenguardGold: false,
        greenguardGoldExpires: '',
        greenguardGoldUrl: '',
        hasIndoorAdvantage: false,
        indoorAdvantageExpires: '',
        indoorAdvantageUrl: '',
        hasIndoorAdvantageGold: false,
        indoorAdvantageGoldExpires: '',
        indoorAdvantageGoldUrl: '',
    };
};

const buildMergedProduct = (
    internal: InternalProduct,
    external: ExternalProduct,
): Product => {
    const highestSection = external.mfSectionLevel1 || external.mfSection;

    const initialMf = getExternalInitialMfFields(external);
    const merged = {
        ...external,
        ...internal,
        dateAdded: internal.dateAdded,
        lastUpdated: internal.lastUpdated,
        product: internal,
        projectProductId: internal.id,
        lowestLevelNumber: initialMf.number || DEFAULT_MF_SECTION.number,
        lowestLevelLabel: initialMf.label || DEFAULT_MF_SECTION.label,
        ratingSystems: external.ratingSystems ?? [],
        mfSection: external.mfSection || highestSection || DEFAULT_MF_SECTION,
        mfDivision: external.mfDivision || DEFAULT_MF_DIVISION,
    } as Product;
    return normalizeProductMasterFormat(merged);
};

/**
 * True when the product row has real TC catalog data (or is a manual OutsideTC product).
 * Reporting CMF used to receive only TC API rows — skip merge fallbacks for missing catalog hits.
 */
export function hasTransparencyCatalogPayload(product: Product): boolean {
    if (product.storage === 'OutsideTC' || product.type === 'userProducts') {
        return true;
    }
    if (product.company?.trim()) {
        return true;
    }
    if (Array.isArray(product.disclosures) && product.disclosures.length > 0) {
        return true;
    }
    return (
        product.hasEpd ||
        product.hasHpd ||
        product.hasDeclare ||
        product.hasC2c ||
        product.hasC2cMHC ||
        product.hasLpc
    );
}

export const mergeInternalProductsWithExternal = (
    internalProducts: InternalProduct[],
    externalProducts: ExternalProduct[] | null | undefined,
): Product[] => {
    const externalById = new Map(
        dedupeExternalCatalogProducts(externalProducts ?? []).map((product) => [product.id, product]),
    );
    return internalProducts.reduce((merged, internal) => {
        const external =
            externalById.get(internal.productId) ?? createFallbackExternalProduct(internal);
        merged.push(buildMergedProduct(internal, external));
        return merged;
    }, [] as Product[]);
};
